<template>
  <section class="chart_card">
    <div class="chart_card__header">
      <div class="chart_card__title_group">
        <span class="chart_card__marker" :style="{ background: accent_color }"></span>
        <h3>{{ title }}</h3>
      </div>
      <span class="chart_card__status">{{ chart_status_label }}</span>
    </div>

    <div ref="chart_container" class="chart_card__body"></div>
  </section>
</template>

<script>
import * as echarts from '../echats.js';
import { resolveApiUrl } from '../utils/api';

export default {
  name: 'MonitoringChartCard',
  props: {
    title: {
      type: String,
      required: true
    },
    chart_type: {
      type: String,
      required: true
    },
    accent_color: {
      type: String,
      default: '#62d9ff'
    },
    empty_message: {
      type: String,
      default: '待接入监测数据'
    },
    api_endpoint: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      chart_instance: null,
      category_axis: [],
      value_series: [],
      chart_legend: [],
      chart_meta: {},
      chart_status: 'EMPTY'
    };
  },
  computed: {
    chart_status_label() {
      return this.chart_status === 'LIVE' ? '已接入' : '待接入';
    }
  },
  mounted() {
    this.initialize_chart();
    this.load_chart_data();
    window.addEventListener('resize', this.handle_resize);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handle_resize);
    this.chart_instance?.dispose();
    this.chart_instance = null;
  },
  methods: {
    initialize_chart() {
      if (!this.$refs.chart_container) {
        return;
      }

      this.chart_instance = echarts.init(this.$refs.chart_container);
      this.render_chart();
    },
    async load_chart_data() {
      // 保留原有 fetch 工作模式，但当前专题大屏未接入对应遥感接口时先展示空图。
      if (!this.api_endpoint) {
        this.chart_status = 'EMPTY';
        this.render_chart();
        return;
      }

      try {
        const response = await fetch(resolveApiUrl(this.api_endpoint));
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const payload = await response.json();
        this.category_axis = Array.isArray(payload.xAxis) ? payload.xAxis : [];
        this.value_series = Array.isArray(payload.series) ? payload.series : [];
        this.chart_legend = Array.isArray(payload.legend) ? payload.legend : [];
        this.chart_meta = payload && typeof payload.meta === 'object' && payload.meta !== null ? payload.meta : {};
        this.chart_status = this.value_series.length ? 'LIVE' : 'EMPTY';
      } catch (error) {
        console.error(`Failed to load chart data for ${this.title}:`, error);
        this.chart_status = 'EMPTY';
        this.category_axis = [];
        this.value_series = [];
        this.chart_legend = [];
        this.chart_meta = {};
      }

      this.render_chart();
    },
    render_chart() {
      if (!this.chart_instance) {
        return;
      }

      this.chart_instance.setOption(this.build_chart_option(), true);
    },
    build_chart_option() {
      const option_builders = {
        construction_trend: this.build_construction_trend_option,
        ndvi_trend: this.build_ndvi_trend_option,
        eco_coordination_scatter: this.build_eco_coordination_scatter_option,
        water_change_compare: this.build_water_change_compare_option,
        risk_ranking: this.build_risk_ranking_option
      };

      const builder = option_builders[this.chart_type] || this.build_construction_trend_option;
      return builder();
    },
    build_common_graphic() {
      const has_data = this.category_axis.length || this.value_series.length;
      if (has_data) {
        return [];
      }

      return [
        {
          type: 'text',
          left: 'center',
          top: 'middle',
          style: {
            text: this.empty_message,
            fill: '#8db4c8',
            font: '13px "Microsoft YaHei"',
            textAlign: 'center',
            lineHeight: 22
          }
        }
      ];
    },
    build_base_option() {
      return {
        animationDuration: 700,
        animationDurationUpdate: 350,
        backgroundColor: 'transparent',
        grid: {
          left: '12%',
          right: '8%',
          top: '18%',
          bottom: '14%',
          containLabel: true
        },
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(12, 20, 30, 0.96)',
          borderWidth: 0,
          textStyle: {
            color: '#edf7ff'
          }
        },
        graphic: this.build_common_graphic()
      };
    },
    build_construction_trend_option() {
      return {
        ...this.build_base_option(),
        xAxis: {
          type: 'category',
          data: this.category_axis,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.35)'
            }
          },
          axisLabel: {
            color: '#cfe8f6'
          }
        },
        yAxis: {
          type: 'value',
          name: 'construction_area',
          nameTextStyle: {
            color: '#8db4c8'
          },
          axisLabel: {
            color: '#cfe8f6'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: 'construction_area',
            type: 'line',
            smooth: true,
            data: this.value_series,
            symbolSize: 8,
            lineStyle: {
              color: '#62d9ff',
              width: 3
            },
            itemStyle: {
              color: '#62d9ff'
            },
            areaStyle: {
              color: 'rgba(98, 217, 255, 0.12)'
            }
          }
        ]
      };
    },
    build_ndvi_trend_option() {
      // NDVI 图既兼容旧的单序列格式，也兼容“全市 + 各县”多序列接口。
      const has_multi_series = this.value_series.every(series_item => (
        series_item &&
        typeof series_item === 'object' &&
        Array.isArray(series_item.data)
      ));

      const ndvi_series = has_multi_series
        ? this.value_series.map((series_item, series_index) => ({
          ...series_item,
          type: series_item.type || 'line',
          smooth: typeof series_item.smooth === 'boolean' ? series_item.smooth : true,
          connectNulls: true,
          emphasis: {
            focus: 'series',
            ...(series_item.emphasis || {})
          },
          lineStyle: {
            width: series_index === 0 ? 4 : 1.5,
            opacity: series_index === 0 ? 1 : 0.5,
            ...(series_item.lineStyle || {})
          },
          itemStyle: {
            opacity: series_index === 0 ? 1 : 0.72,
            ...(series_item.itemStyle || {})
          }
        }))
        : [
          {
            name: 'ndvi',
            type: 'line',
            smooth: true,
            data: this.value_series,
            symbolSize: 8,
            lineStyle: {
              color: '#3af3cf',
              width: 3
            },
            itemStyle: {
              color: '#3af3cf'
            },
            areaStyle: {
              color: 'rgba(58, 243, 207, 0.1)'
            }
          }
        ];

      return {
        ...this.build_base_option(),
        legend: has_multi_series ? {
          type: 'scroll',
          top: 6,
          left: 8,
          right: 8,
          pageIconColor: '#8db4c8',
          pageTextStyle: {
            color: '#8db4c8'
          },
          textStyle: {
            color: '#8db4c8',
            fontSize: 10
          },
          data: this.chart_legend.length ? this.chart_legend : ndvi_series.map(series_item => series_item.name)
        } : undefined,
        grid: has_multi_series ? {
          left: '12%',
          right: '8%',
          top: '26%',
          bottom: '14%',
          containLabel: true
        } : this.build_base_option().grid,
        xAxis: {
          type: 'category',
          data: this.category_axis,
          boundaryGap: false,
          axisLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.35)'
            }
          },
          axisLabel: {
            color: '#cfe8f6'
          }
        },
        yAxis: {
          type: 'value',
          name: 'ndvi',
          min: 0,
          max: 1,
          nameTextStyle: {
            color: '#8db4c8'
          },
          axisLabel: {
            color: '#cfe8f6'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        series: [
          ...ndvi_series
        ]
      };
    },
    build_eco_coordination_scatter_option() {
      const has_multi_series = this.value_series.every(series_item => (
        series_item &&
        typeof series_item === 'object' &&
        Array.isArray(series_item.data)
      ));

      const scatter_series = has_multi_series
        ? this.value_series.map(series_item => ({
          ...series_item,
          type: series_item.type || 'scatter',
          emphasis: {
            focus: 'series',
            ...(series_item.emphasis || {})
          }
        }))
        : [
          {
            name: 'coordination',
            type: 'scatter',
            data: Array.isArray(this.value_series[0]) ? this.value_series : [],
            symbolSize: 12,
            itemStyle: {
              color: '#ffc857',
              shadowBlur: 16,
              shadowColor: 'rgba(255, 200, 87, 0.28)'
            }
          }
        ];

      return {
        ...this.build_base_option(),
        legend: has_multi_series ? {
          top: 6,
          left: 8,
          textStyle: {
            color: '#8db4c8',
            fontSize: 10
          },
          data: this.chart_legend.length ? this.chart_legend : scatter_series.map(series_item => series_item.name)
        } : undefined,
        grid: has_multi_series ? {
          left: '9%',
          right: '14%',
          top: '24%',
          bottom: '14%',
          containLabel: true
        } : this.build_base_option().grid,
        tooltip: {
          ...this.build_base_option().tooltip,
          trigger: 'item',
          formatter: params => {
            const point_data = params.data || {};
            const point_value = Array.isArray(point_data.value) ? point_data.value : [];
            const point_name = point_data.name || params.name || '-';
            const construction_area = point_value[0] ?? '-';
            const coordination = point_data.coordination ?? point_value[1] ?? '-';
            const ndvi_value = point_data.ndvi ?? '-';
            const eco_quality = point_data.eco_quality ?? '-';

            return [
              `<strong>${point_name}</strong>`,
              `建设用地面积：${construction_area}`,
              `协调度指数：${coordination}`,
              `NDVI：${ndvi_value}`,
              `生态质量：${eco_quality}`
            ].join('<br/>');
          }
        },
        xAxis: {
          type: 'value',
          name: this.chart_meta.x_axis_name || 'construction_area',
          min: this.chart_meta.x_min,
          max: this.chart_meta.x_max,
          nameTextStyle: {
            color: '#8db4c8'
          },
          axisLabel: {
            color: '#cfe8f6'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        yAxis: {
          type: 'value',
          name: this.chart_meta.y_axis_name || 'coordination',
          min: typeof this.chart_meta.y_min === 'number' ? this.chart_meta.y_min : 0,
          max: typeof this.chart_meta.y_max === 'number' ? this.chart_meta.y_max : 1,
          nameTextStyle: {
            color: '#8db4c8'
          },
          axisLabel: {
            color: '#cfe8f6',
            formatter: value => Number(value).toFixed(2)
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        series: [
          ...scatter_series
        ]
      };
    },
    build_water_change_compare_option() {
      const has_split_layout = this.chart_meta.chart_layout === 'annual_county_split'
        && Array.isArray(this.chart_meta.annual_axis)
        && Array.isArray(this.chart_meta.county_axis)
        && this.value_series.every(series_item => (
          series_item &&
          typeof series_item === 'object' &&
          Array.isArray(series_item.data)
        ));

      if (has_split_layout) {
        return {
          animationDuration: 700,
          animationDurationUpdate: 350,
          backgroundColor: 'transparent',
          legend: {
            top: 10,
            left: 8,
            right: 8,
            textStyle: {
              color: '#8db4c8',
              fontSize: 10
            },
            data: this.chart_legend.length ? this.chart_legend : this.value_series.map(series_item => series_item.name)
          },
          tooltip: {
            trigger: 'item',
            backgroundColor: 'rgba(12, 20, 30, 0.96)',
            borderWidth: 0,
            textStyle: {
              color: '#edf7ff'
            },
            formatter: params => {
              if (params.seriesIndex === 2) {
                const county_record = this.chart_meta.county_records?.[params.dataIndex] || {};
                return [
                  `<strong>${county_record.county_name || params.name || '-'}</strong>`,
                  `水体总面积：${county_record.total_water_area ?? '-'} km²`,
                  `河流：${county_record.river_area ?? '-'} km²`,
                  `水库湖泊：${county_record.reservoir_lake_area ?? '-'} km²`,
                  `湿地：${county_record.wetland_area ?? '-'} km²`,
                  `占全市比重：${county_record.share_ratio ?? '-'}%`
                ].join('<br/>');
              }

              const annual_record = this.chart_meta.annual_records?.[params.dataIndex] || {};
              return [
                `<strong>${annual_record.year || params.name || '-'}</strong>`,
                `水体总面积：${annual_record.total_water_area ?? '-'} km²`,
                `河流：${annual_record.river_area ?? '-'} km²`,
                `水库湖泊：${annual_record.reservoir_lake_area ?? '-'} km²`,
                `湿地：${annual_record.wetland_area ?? '-'} km²`,
                `同比增量：${annual_record.yearly_change ?? '-'} km²`,
                `同比增速：${annual_record.yearly_rate ?? '-'}%`
              ].join('<br/>');
            }
          },
          graphic: this.build_common_graphic(),
          grid: [
            {
              left: '12%',
              right: '12%',
              top: '27%',
              height: '29%'
            },
            {
              left: '24%',
              right: '10%',
              top: '70%',
              bottom: '10%'
            }
          ],
          xAxis: [
            {
              type: 'category',
              gridIndex: 0,
              data: this.chart_meta.annual_axis,
              name: this.chart_meta.top_x_axis_name || '',
              nameGap: 18,
              nameTextStyle: {
                color: '#8db4c8'
              },
              axisLine: {
                lineStyle: {
                  color: 'rgba(141, 180, 200, 0.35)'
                }
              },
              axisLabel: {
                color: '#cfe8f6',
                fontSize: 10
              }
            },
            {
              type: 'value',
              gridIndex: 1,
              name: this.chart_meta.bottom_x_axis_name || 'water_area',
              nameGap: 18,
              nameTextStyle: {
                color: '#8db4c8'
              },
              axisLine: {
                lineStyle: {
                  color: 'rgba(141, 180, 200, 0.35)'
                }
              },
              axisLabel: {
                color: '#cfe8f6',
                fontSize: 10
              },
              splitLine: {
                lineStyle: {
                  color: 'rgba(141, 180, 200, 0.12)',
                  type: 'dashed'
                }
              }
            }
          ],
          yAxis: [
            {
              type: 'value',
              gridIndex: 0,
              name: this.chart_meta.top_left_y_axis_name || 'water_area',
              nameGap: 22,
              nameTextStyle: {
                color: '#8db4c8'
              },
              axisLabel: {
                color: '#cfe8f6',
                fontSize: 10
              },
              splitLine: {
                lineStyle: {
                  color: 'rgba(141, 180, 200, 0.12)',
                  type: 'dashed'
                }
              }
            },
            {
              type: 'value',
              gridIndex: 0,
              name: this.chart_meta.top_right_y_axis_name || 'yoy_rate',
              position: 'right',
              nameGap: 22,
              nameTextStyle: {
                color: '#8db4c8'
              },
              axisLabel: {
                color: '#cfe8f6',
                fontSize: 10,
                formatter: value => `${value}%`
              },
              splitLine: {
                show: false
              }
            },
            {
              type: 'category',
              gridIndex: 1,
              data: this.chart_meta.county_axis,
              inverse: true,
              axisLine: {
                show: false
              },
              axisTick: {
                show: false
              },
              axisLabel: {
                color: '#cfe8f6',
                fontSize: 10
              }
            }
          ],
          series: this.value_series.map((series_item, series_index) => ({
            ...series_item,
            ...(series_index === 2
              ? {
                xAxisIndex: 1,
                yAxisIndex: 2
              }
              : {
                xAxisIndex: 0,
                yAxisIndex: series_index === 0 ? 0 : 1
              })
          }))
        };
      }

      return {
        ...this.build_base_option(),
        legend: {
          top: 8,
          textStyle: {
            color: '#8db4c8'
          }
        },
        xAxis: {
          type: 'category',
          data: this.category_axis,
          axisLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.35)'
            }
          },
          axisLabel: {
            color: '#cfe8f6'
          }
        },
        yAxis: {
          type: 'value',
          name: 'ndwi',
          nameTextStyle: {
            color: '#8db4c8'
          },
          axisLabel: {
            color: '#cfe8f6'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: 'water_area',
            type: 'bar',
            data: this.value_series,
            barWidth: 18,
            itemStyle: {
              color: '#4bb8ff',
              borderRadius: [6, 6, 0, 0]
            }
          }
        ]
      };
    },
    build_risk_ranking_option() {
      return {
        ...this.build_base_option(),
        grid: {
          left: '20%',
          right: '8%',
          top: '16%',
          bottom: '12%'
        },
        xAxis: {
          type: 'value',
          axisLabel: {
            color: '#cfe8f6'
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(141, 180, 200, 0.12)',
              type: 'dashed'
            }
          }
        },
        yAxis: {
          type: 'category',
          inverse: true,
          data: this.category_axis,
          axisLabel: {
            color: '#cfe8f6'
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          }
        },
        series: [
          {
            name: 'risk_level',
            type: 'bar',
            data: this.value_series,
            barWidth: 14,
            itemStyle: {
              color: '#ff7c7c',
              borderRadius: 999
            }
          }
        ]
      };
    },
    handle_resize() {
      this.chart_instance?.resize();
    }
  }
};
</script>

<style scoped>
.chart_card {
  position: relative;
  overflow: hidden;
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  background: rgba(8, 17, 26, 0.92);
  box-shadow: 0 14px 28px rgba(0, 0, 0, 0.2);
  padding: 10px 12px 8px;
}

.chart_card::before {
  content: "";
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(99, 163, 201, 0.035), transparent 16%),
    linear-gradient(90deg, rgba(255, 255, 255, 0.012), transparent 28%);
}

.chart_card::after {
  content: "";
  position: absolute;
  left: 12px;
  right: 12px;
  top: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(79, 194, 220, 0.55), transparent);
  opacity: 0.72;
}

.chart_card__header,
.chart_card__body {
  position: relative;
  z-index: 1;
}

.chart_card__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding-bottom: 4px;
}

.chart_card__title_group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.chart_card__marker {
  width: 3px;
  height: 12px;
  border-radius: 999px;
}

.chart_card h3 {
  margin: 0;
  color: var(--text_primary);
  font-size: 14px;
  line-height: 1.2;
  font-weight: 500;
  letter-spacing: 0.03em;
}

.chart_card__status {
  color: var(--text_secondary);
  font-size: 10px;
  letter-spacing: 0.08em;
}

.chart_card__body {
  width: 100%;
  flex: 1;
  min-height: 0;
  margin-top: 6px;
}

@media (max-width: 1200px) {
  .chart_card__body {
    min-height: 220px;
  }
}
</style>
